// src/lib/csvImporters.js
import Papa from "papaparse";
import { db } from "../firebase";
import {
  doc, getDoc, updateDoc, collection, getDocs, addDoc
} from "firebase/firestore";
import { FUTURES_PRODUCTS, futuresCodeOf } from "../constants/instruments";

// ------------------------------
// Small, shared helpers
// ------------------------------
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const round1 = (n) => Math.round((Number(n) + Number.EPSILON) * 10) / 10;

// --- Debug helpers (noop unless enabled) ---
const DEBUG_IMPORT =
  typeof window !== "undefined" &&
  window.localStorage &&
  window.localStorage.getItem("debugImport") === "1";

const dlog = (...args) => { if (DEBUG_IMPORT) console.log("[csvImport]", ...args); };
const dgroup = (label) => { if (DEBUG_IMPORT && console.groupCollapsed) console.groupCollapsed(label); };
const dgroupEnd = () => { if (DEBUG_IMPORT && console.groupEnd) console.groupEnd(); };

const parseSheetNumber = (value) => {
  if (typeof value === "number") return value;
  if (value == null) return 0;
  const s = String(value).trim();
  const isNegative = /^\(.*\)$/.test(s) || /^-/.test(s);
  const n = parseFloat(s.replace(/[^\d.]/g, "")) || 0;
  return isNegative ? -Math.abs(n) : n;
};

// Tolerant field lookup — trims/normalizes header keys and supports simple aliases
const normalizeKey = (k) =>
  String(k || "")
    .normalize("NFKC")
    .replace(/\u00A0/g, " ")    // NBSP → space
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const pickField = (row, ...names) => {
  // Build a once-per-row normalized key map
  const normMap =
    row.__normMap ||
    Object.fromEntries(Object.keys(row).map((k) => [normalizeKey(k), k]));
  row.__normMap = normMap;

  for (const n of names) {
    const rawKey = normMap[normalizeKey(n)];
    if (rawKey == null) continue;
    const val = row[rawKey];
    if (val != null && String(val).trim() !== "") return val;
  }
  return "";
};

// Simple CSV parser: supports quoted fields and commas
function parseCsvText(text) {
  const lines = text
    .replace(/\r/g, "")
    .split("\n")
    .filter((l) => l.trim() !== "");
  if (!lines.length) return [];

  const parseLine = (line) => {
    const out = [];
    let cur = "";
    let inQ = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i + 1] === '"') {
          // Escaped quote
          cur += '"';
          i++;
        } else if (ch === '"') {
          inQ = false;
        } else {
          cur += ch;
        }
      } else {
        if (ch === '"') {
          inQ = true;
        } else if (ch === ",") {
          out.push(cur);
          cur = "";
        } else {
          cur += ch;
        }
      }
    }
    out.push(cur);
    return out;
  };

  const headers = parseLine(lines[0]).map((h) => h.trim());
  return lines.slice(1).map((l) => {
    const cols = parseLine(l);
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cols[i] ?? "";
    });
    return row;
  });
}

// Parse "H:MM", "H:MM:SS", optionally with AM/PM, into seconds
function parseTimeToSeconds(t) {
  if (!t) return -1;
  const m = String(t)
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([APMapm]{2}))?$/);
  if (!m) return -1;

  let [, h, min, sec, ap] = m;
  let hour = parseInt(h, 10);
  const minutes = parseInt(min, 10);
  const seconds = sec ? parseInt(sec, 10) : 0;

  if (ap) {
    const ampm = ap.toUpperCase();
    if (ampm === "PM" && hour < 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
  }

  return hour * 3600 + minutes * 60 + seconds;
}

// Compute duration as "H:MM:SS" between two times
function computeDurationHMS(entryStr, exitStr) {
  const start = parseTimeToSeconds(entryStr);
  const end = parseTimeToSeconds(exitStr);
  if (start < 0 || end < 0) return "";

  let diff = end - start;
  if (diff < 0) diff += 24 * 3600; // handle crossing midnight

  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;

  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}


function computePercentGrowth({
  isPropEval,
  isPropFunded,
  reward,          // Actual Reward for this trade (can be +/-)
  rbBefore,        // Running Balance BEFORE the trade
  prbBefore,       // Prop Running Balance BEFORE the trade
}) {
  // Eval → divide by PRB before trade; Funded/Retail → divide by RB before trade
  const base = isPropEval ? prbBefore : rbBefore;
  const den = (Number.isFinite(base) && base !== 0) ? base : 1;
  return Number.isFinite(reward) ? (reward / den) * 100 : 0;
}

// Wrapper that logs what base we used (RB vs PRB) and the computed %.
const computePercentGrowthDebug = (args) => {
  const out = computePercentGrowth(args);
  const baseUsed = args.isPropEval ? "PRB (evaluation)" : "RB (funded/retail)";
  dlog("computePercentGrowth", {
    baseUsed,
    isPropEval: args.isPropEval,
    isPropFunded: args.isPropFunded,
    reward: args.reward,
    rbBefore: args.rbBefore,
    prbBefore: args.prbBefore,
    percent: out
  });
  return out;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toAmPm = (timeStr) => {
  if (!timeStr) return "";
  const parts = String(timeStr).split(":");
  const hour = parseInt(parts[0], 10);
  const min = parseInt(parts[1] || "0", 10);
  if (isNaN(hour) || isNaN(min)) return "";
  const ampm = hour >= 12 ? "PM" : "AM";
  const hh = hour % 12 || 12;
  return `${hh}:${min.toString().padStart(2, "0")} ${ampm}`;
};

const splitDateTime = (str) => {
  if (!str) return { date: "", time: "" };
  const parts = String(str).trim().split(" ");
  if (parts.length < 2) return { date: parts[0], time: "" };
  return { date: parts[0], time: parts[1] };
};

const calculateDuration = (start, end) => {
  if (!start || !end) return "";
  const s = new Date(start); const e = new Date(end);
  if (isNaN(s) || isNaN(e)) return "";
  let diffMins = Math.abs(e - s) / (1000 * 60);
  const h = Math.floor(diffMins / 60);
  const m = Math.floor(diffMins % 60);
  const sec = Math.round((diffMins % 1) * 60);
  return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const getProduct = (val = "") => {
  const s = String(val || "");
  if (!s) return "";
  // "MYM..." retain 3 chars for micro codes, otherwise 2 chars
  return s[0] === "M" ? s.slice(0, 3) : s.slice(0, 2);
};

const money = (v) => {
  if (v == null || v === "") return 0;
  const str = String(v).trim();
  const neg = /^\(.*\)$/.test(str) || /^-/.test(str);
  const n = Number(str.replace(/[^\d.]/g, ""));
  return neg ? -Math.abs(n) : n;
};

// Put near the other helpers (e.g., under money()/getProduct()):

// Normalize a header key once (case/space/punct-insensitive)
const _normKey = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Build a once-per-row lookup from normalized -> actual key
const _normMapOf = (row) => {
  if (row.__nk) return row.__nk;
  const m = new Map();
  for (const k of Object.keys(row || {})) m.set(_normKey(k), k);
  row.__nk = m;
  return m;
};

// Get a string-like value by trying a list of possible header names
const valFrom = (row, names, fallback = "") => {
  const map = _normMapOf(row);
  for (const name of names) {
    const actual = map.get(_normKey(name));
    if (actual !== undefined) {
      const v = row[actual];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
  }
  return fallback;
};

// Get a numeric value (parsed like the rest of the importer)
const numFrom = (row, names) => {
  const raw = valFrom(row, names, "");
  const n = parseSheetNumber(raw);
  return Number.isFinite(n) ? n : NaN;
};


// ------------------------------
// Firestore helpers
// ------------------------------
async function getAccountSettings(userId, accountId) {
  if (!userId || !accountId) return null;
  const ref = doc(db, "users", userId, "accounts", accountId);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}
async function updateAccountSettings(userId, accountId, patch) {
  if (!userId || !accountId) return;
  const ref = doc(db, "users", userId, "accounts", accountId);
  await updateDoc(ref, patch);
}
async function fetchJournalRows(userId, accountId) {
  if (!userId || !accountId) return [];
  const j = collection(db, "users", userId, "accounts", accountId, "journal");
  const snap = await getDocs(j);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
async function appendTrade(userId, accountId, trade) {
  if (!userId || !accountId) return;
  const j = collection(db, "users", userId, "accounts", accountId, "journal");
  await addDoc(j, trade);
}

// Case-insensitive text picker over possible header aliases
const pickTextCI = (row, candidates) => {
  const keys = Object.keys(row);
  for (const name of candidates) {
    const found = keys.find(
      k => k.trim().toLowerCase() === String(name).trim().toLowerCase()
    );
    if (!found) continue;
    const v = row[found];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return "";
};


// ------------------------------
// Dedup fingerprints (stable)
// ------------------------------

const buildFingerprintQuantower = (r) => {
  const s = (names) => String(valFrom(r, names, "")).trim().toLowerCase();
  const n = (names) => {
    const v = numFrom(r, names);
    return Number.isFinite(v) ? String(v) : "";
  };

  return [
    "quantower",
    s(["Account"]),
    s(["Symbol"]),
    s(["Side"]),
    s(["Date/Time"]),
    n(["Quantity"]),
    n(["Gross P/L", "Net P/L"]),
    n(['Gross P/L,ticks']),
    s(["Trade ID"]),
    s(["Order ID"]),
  ].join("|");
};

const buildFingerprintTopstep = (r) =>
  `topstep|${r.Id}|${r.ContractName}|${r.EnteredAt}|${r.ExitedAt}|${r.Size}|${r.PnL}`.toLowerCase();

const buildFingerprintTradovate = (r) =>
  `tradovate|${r.buyFillId}|${r.sellFillId}|${r.symbol || r.contract}|${r.qty || r.quantity}|${r.pnl}`.toLowerCase();

  const buildFingerprintTrapound = (r) => {
    const s = (names) => String(valFrom(r, names, "")).trim().toLowerCase();
    const n = (names) => {
      const v = numFrom(r, names);
      return Number.isFinite(v) ? String(v) : "";
    };

    // Include Entry/Exit Time to improve uniqueness across same-day/same-symbol trades
    return [
      s(["Date", "Trade Date"]),
      s(["Entry Time", "Entry"]),
      s(["Exit Time", "Exit"]),
      s(["Product", "Symbol", "Instrument"]),
      s(["Position", "Side"]),
      s(["Size", "Qty", "Quantity", "Lots"]),
      n(["Actual Reward", "Reward"]),
      n(["Actual Balance", "Balance", "Account Balance"])
    ].join("|");
  };


  const buildFingerprintNinjatrader = (r) => {
    const s = (names) => String(valFrom(r, names, "")).trim().toLowerCase();
    const n = (names) => {
      const v = numFrom(r, names);
      return Number.isFinite(v) ? String(v) : "";
    };

    return [
      "ninjatrader",
      s(["Trade number", "Trade #", "Trade"]),
      s(["Instrument", "Symbol"]),
      s(["Entry time", "Entry Time"]),
      s(["Exit time",  "Exit Time"]),
      n(["Qty", "Quantity", "Size"]),
      n(["Profit", "Net profit", "P&L", "PnL"]),
    ].join("|");
  };


/**
 * Tick Value lookup from FUTURES_PRODUCTS (e.g., "YM - US30 - $5.00").
 * We capture the $value and key by the short code produced by futuresCodeOf().
 */
const tickValueFor = (() => {
  const cache = new Map();
  return (codeOrName) => {
    const key = String(getProduct(codeOrName) || codeOrName || "").toUpperCase();
    if (!key) return null;
    if (cache.has(key)) return cache.get(key);
    let val = null;
    for (const [full, label] of (FUTURES_PRODUCTS || [])) {
      const c = futuresCodeOf(full);
      if (String(c).toUpperCase() === key) {
        const m = (`${full} ${label || ""}`).match(/\$([0-9]+(?:\.[0-9]+)?)/);
        if (m) val = Number(m[1]);
        break;
      }
    }
    cache.set(key, val);
    return val;
  };
})();

const isForexProduct = (prod) => {
  const s = String(prod || "").trim();
  if (!s) return false;
  if (s.includes("/")) return true;               // "EUR/USD"
  return /^[A-Z]{6}$/.test(s);                    // "EURUSD"
};

const tickSizeFor = (codeOrName) => {
  const c = String(getProduct(codeOrName) || codeOrName || "").toUpperCase();
  switch (c) {
    // Equity indices
    case "ES":
    case "MES":
    case "NQ":
    case "MNQ":
      return 0.25;

    case "YM":
    case "MYM":
      return 1;

    case "RT":
    case "M2K":
      return 0.1;

    // Metals / energy / gas
    case "GC":
    case "MGC":
      return 0.1;

    case "NG":
    case "MNG":
      return 0.001;

    case "CL":
    case "MCL":
      return 0.01;

    case "SI":
    case "SIL":
      return 0.005;

    case "HG":
    case "MHG":
      return 0.0005;

    // Currency futures (best‑effort; if not present, we’ll just skip ticks/P&L)
    case "6E":
    case "M6E":
      return 0.00005;

    case "6B":
    case "M6B":
    case "6A":
    case "M6A":
    case "6C":
    case "M6C":
      return 0.0001;

    case "6J":
    case "M6J":
      return 0.000001;

    default:
      return null;
  }
};

const pipSizeFor = (symbol) => {
  const s = String(symbol || "").toUpperCase().replace("/", "");
  return /JPY$/.test(s) ? 0.01 : 0.0001;
};


function resolveAccountTypeFromAccount(account) {
  if (!account || typeof account !== "object") return null;
  const s = account.settings || {};

  const vals = [
    account.type,
    account.Type,
    account.accountType,
    account["Account Type"],
    account["Trade Mode"],
    account.Platform,
    s.type,
    s.Type,
    s.accountType,
    s["Account Type"],
    s["Trade Mode"],
    s.Platform,
  ]
    .filter(Boolean)
    .map(v => String(v).toLowerCase());

  if (!vals.length) return null;
  const joined = vals.join(" ");

  if (joined.includes("forex") || joined.includes("fx")) return "forex";
  if (joined.includes("future")) return "futures";
  return null;
}


// ======================================================
// Public API
// ======================================================
export async function importCsvByPlatform({ userId, accountId, platform, file, finalBalance }) {
  const text = await file.text();
  const { data: rows } = Papa.parse(text, { header: true, skipEmptyLines: "greedy" });

  // Existing rows → dedupe
  const existing = await fetchJournalRows(userId, accountId);
  const existingFingerprints = new Set(existing.map(r => r.importFingerprint).filter(Boolean));

  // Account context (look in BOTH nested 'settings' and the account doc top-level)
  const account = await getAccountSettings(userId, accountId);
  const s = account?.settings || {};
    const pick = (k) =>
      (account?.settings && account.settings[k] !== undefined
        ? account.settings[k]
        : account?.[k]);

  const acctTypeRaw = resolveAccountTypeFromAccount(account);
  const acctType    = String(acctTypeRaw || "").toLowerCase();
  const accountIsForex   = acctType === "forex" || acctType === "fx";
  const accountIsFutures = acctType === "futures";

  const rowAllowedForAccount = (rowIsForex) => {
    if (accountIsForex)   return !!rowIsForex;      // forex acct → require forex row
    if (accountIsFutures) return !rowIsForex;       // futures acct → require non‑forex row
    return true;                                    // unknown acct type → don’t filter
  };

  // Starting ledgers
  const startRB  = Number(pick("Running Balance") || 0);
  const startPRB = Number(
    pick("Prop Running Balance") ??
    Math.max(0, Number(pick("Initial Balance") || 0) - Number(pick("Maximum Loss Limit") || 0))
  );

  // Derive account flags from merged settings (tolerant field names)
  const fundingText = String(
    s["Funding Type"] ?? s["Trade Mode"] ?? s["Account Type"] ?? s["type"] ?? ""
  ).toLowerCase();

  const isPropFundedRaw = Boolean(s.isPropFunded || /funded|live/.test(fundingText));
  const isPropEvalRaw   = Boolean(s.isPropEval   || /eval|evaluation/.test(fundingText));


    // Prop flags (robust; read both booleans *and* text fields)
  const propFirm       = Boolean(s.isProp || s.isPropFirm || /prop/.test(fundingText));
  // Clear funded detection (booleans or text)
  const isPropFunded   = Boolean(s.isPropFunded || /funded|live/.test(fundingText));

  // Many prop programs use different words for eval phases
  const isExplicitEval = Boolean(
    s.isPropEval || /eval|evaluation|challenge|combine|audition|practice|demo/.test(fundingText)
  );

  // **Key change**: treat “prop-like & not funded” as evaluation
  const isPropEval     = !isPropFunded && (isExplicitEval || propFirm);

  // Running ledgers (RB/PRB)
  let runningBalance     = startRB;
  let propRunningBalance = startPRB;

  let imported = 0, skipped = 0;

  // ------------------------------
  // TopStep / Tradovate (Futures)
  // ------------------------------
  if (platform === "topstep" || platform === "tradovate") {
    const hasFinal      = typeof finalBalance === "string" && finalBalance.trim() !== "" && isFinite(Number(finalBalance));
    const finalRBNumber = hasFinal ? Number(finalBalance) : NaN;

    const firstNonEmpty = (...vals) => vals.find(v => v !== undefined && v !== null && String(v).trim() !== "") ?? "";

    for (const row of rows) {
         // Futures‑only importer: if account is forex‑only, skip trade rows
         if (!rowAllowedForAccount(false)) { // false => this is a futures row
           skipped++;
           continue;
         }
      let actReward = 0;
      let fp = "";

if (platform === "topstep") {
        // --- Dedupe key using flexible column names
        const tradeId = valFrom(row, ["Id", "TradeId", "Trade ID", "Ticket", "OrderID"]);
        if (!tradeId) continue;
        
        fp = buildFingerprintTopstep({ ...row, Id: tradeId }); // Ensure fp builder has the ID
        if (existingFingerprints.has(fp)) { skipped++; continue; }

        // --- Net PnL
        const pnl  = money(valFrom(row, ["PnL", "P&L", "Profit", "NetPnL", "RealizedPnL"]));
        const fees = Math.abs(money(valFrom(row, ["Fees", "Fee", "Commissions", "Commission", "TotalFees"])));
        actReward  = round2(pnl - fees);

        // --- Times
        const rawEntered = valFrom(row, ["EnteredAt", "Entered At", "EntryTime", "OpenTime"]);
        const rawExited  = valFrom(row, ["ExitedAt", "Exited At", "ExitTime", "CloseTime"]);
        
        const parseDT = (dtStr) => {
            if (!dtStr) return { date: "", time: "" };
            const d = new Date(dtStr.includes("T") ? dtStr : dtStr.replace(" ", "T"));
            if (isNaN(d.getTime())) {
                const parts = String(dtStr).trim().split(/[\sT]+/);
                return { date: parts[0] || "", time: parts[1] || "" };
            }
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const mins = String(d.getMinutes()).padStart(2, '0');
            return { date: `${year}-${month}-${day}`, time: `${hours}:${mins}` };
        };

        const entered = parseDT(rawEntered);
        const exited  = parseDT(rawExited);

        // --- Balances and % growth
        const beforeLedger = round2(runningBalance);
        const growthPct    = computePercentGrowthDebug({
          isPropEval, isPropFunded,
          reward: actReward,
          rbBefore: beforeLedger,
          prbBefore: round2(propRunningBalance),
        });
        const afterLedger  = round2(beforeLedger + actReward);

        const rawContract = valFrom(row, ["ContractName", "Contract Name", "Contract", "Symbol", "Instrument"]);
        const rawType     = String(valFrom(row, ["Type", "Side", "Position"])).toLowerCase();
        const rawSize     = valFrom(row, ["Size", "Qty", "Quantity"]);

        // --- Ticks from net PnL
        const code  = getProduct(rawContract);
        const tv    = tickValueFor(code);
        const qty   = Number(rawSize || 0);
        const ticks = (tv && qty > 0 && Number.isFinite(actReward))
          ? Math.round(actReward / (tv * qty))
          : null;
          
        const entryPriceNum = numFrom(row, ["Entry Price", "EntryPrice", "Avg Entry Price", "Average Entry Price"]);
        const exitPriceNum  = numFrom(row, ["Exit Price", "ExitPrice", "Avg Exit Price", "Average Exit Price"]);

        const tradeObj = {
          "Trade #": Date.now() + imported,
          "Date": entered.date,
          "Product": code,
          "Position": (rawType === "short" || rawType === "sell") ? "Sell" : "Buy",
          "Size": qty,
          "Entry Time": toAmPm(entered.time),
          "Exit Time": toAmPm(exited.time),
          "Duration": calculateDuration(rawEntered, rawExited),

          // PnL & balances
          "Actual Reward": actReward,
          "Balance Before Trade": beforeLedger,               
          "Actual Balance": String(afterLedger.toFixed(2)),   

          "Actual Percent Growth": `${round2(growthPct).toFixed(2)}%`,
          "Percent of Balance": null,

          "Win": actReward > 0,
          "Loss": actReward < 0,

          "Notes": `Imported from TopStep ${tradeId}`,
          "created": new Date(),
          "importFingerprint": fp
        };
        if (ticks !== null) tradeObj["Ticks"] = ticks;
        if (Number.isFinite(entryPriceNum)) tradeObj["Entry Price"] = entryPriceNum;
        if (Number.isFinite(exitPriceNum))  tradeObj["Exit Price"]  = exitPriceNum;

        await appendTrade(userId, accountId, tradeObj);

        // advance ledgers
        runningBalance     = afterLedger;
        propRunningBalance = round2(propRunningBalance + actReward);
      } else {
        // -----------------
        // TRADOVATE branch
        // -----------------
        if (!row.buyFillId || !row.sellFillId) continue;
        fp = buildFingerprintTradovate(row);
        if (existingFingerprints.has(fp)) { skipped++; continue; }

        // pnl provided as "$(…)" or "$…"
        const pnlRaw = String(row.pnl || "");
        const pnlVal = pnlRaw.replace(/[\$,()]/g, "");
        actReward    = pnlRaw.includes("(") ? -Math.abs(Number(pnlVal)) : Number(pnlVal);

        // column variants we’ve seen
        const buyTs  = firstNonEmpty(row.buyFillTime,  row.boughtTimestamp, row.buyTimestamp,  row.entryTime, row.buyTime);
        const sellTs = firstNonEmpty(row.sellFillTime, row.soldTimestamp,   row.sellTimestamp, row.exitTime,  row.sellTime);

        // Split for date; if time part isn't present, fall back
        const entered = splitDateTime(buyTs);
        const exited  = splitDateTime(sellTs);

        const hhmm = (ts) => {
          const d = new Date(ts);
          return isNaN(d) ? "" : `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
        };
        const entryTime = entered.time || hhmm(buyTs);
        const exitTime  = exited.time  || hhmm(sellTs);

        // product/size variants
        const code = getProduct(firstNonEmpty(row.contract, row.symbol));
        const qty  = Number(firstNonEmpty(row.quantity, row.qty, row.Size) || 0);
        // Optional entry/exit prices (Tradovate has several header variants)
        const entryPriceNum = numFrom(row, [
          "Entry Price",
          "EntryPrice",
          "Buy Price",
          "BuyPrice",
          "buyFillPrice",
          "Entry",
        ]);
        const exitPriceNum  = numFrom(row, [
          "Exit Price",
          "ExitPrice",
          "Sell Price",
          "SellPrice",
          "sellFillPrice",
          "Exit",
        ]);


        // balances & % growth
        const beforeLedger = round2(runningBalance);  // always RB in ledger fields
        const growthPct    = computePercentGrowthDebug({
          isPropEval, isPropFunded,
          reward: actReward,
          rbBefore: beforeLedger,
          prbBefore: round2(propRunningBalance),
        });
        const afterLedger  = round2(beforeLedger + actReward);

        // ticks from net PnL (WHOLE numbers)
        const tv    = tickValueFor(code);
        const ticks = (tv && qty > 0 && Number.isFinite(actReward))
          ? Math.round(actReward / (tv * qty))
          : null;

        const tradeObj = {
          "Trade #": Date.now() + imported,
          "Date": formatDate(entered.date || buyTs),
          "Product": code,
          "Position": row.side === "Sell" ? "Sell" : "Buy",
          "Size": qty,
          "Entry Time": toAmPm(entryTime),
          "Exit Time": toAmPm(exitTime),
          "Duration": calculateDuration(buyTs, sellTs),

          "Actual Reward": actReward,
          "Balance Before Trade": beforeLedger,               // always RB
          "Actual Balance": String(afterLedger.toFixed(2)),   // always RB

          "Actual Percent Growth": `${round2(growthPct).toFixed(2)}%`,

          // Risk budget for broker imports -> null
          "Percent of Balance": null,

          "Win": actReward > 0,
          "Loss": actReward < 0,

          "Notes": `Imported from Tradovate ${row.buyFillId}:${row.sellFillId}`,
          "created": new Date(),
          "importFingerprint": fp
        };
        if (ticks !== null) tradeObj["Ticks"] = ticks;
        if (Number.isFinite(entryPriceNum)) tradeObj["Entry Price"] = entryPriceNum;
        if (Number.isFinite(exitPriceNum))  tradeObj["Exit Price"]  = exitPriceNum;


        dgroup(`TRADOVATE import #${imported + 1} buy=${row.buyFillId} sell=${row.sellFillId}`);
        dlog("rawRow", row);
        dlog("flags", { propFirm, isPropFunded, isPropEval });
        dlog("balances", {
          startRB, startPRB,
          beforeLedger,
          propRunningBalanceBefore: round2(propRunningBalance)
        });
        dlog("reward/times", { pnlRaw, actReward, buyTs, sellTs, entryTime, exitTime, duration: calculateDuration(buyTs, sellTs) });
        dlog("growthInputs", { rbBefore: beforeLedger, prbBefore: round2(propRunningBalance), reward: actReward });
        dlog("growthPct", growthPct);
        dlog("ticks", { code, tv, qty, ticks });
        dgroupEnd();

        await appendTrade(userId, accountId, tradeObj);

        runningBalance     = afterLedger;
        propRunningBalance = round2(propRunningBalance + actReward);
      }

      existingFingerprints.add(fp);
      imported++;
    }

    // After all trades: update balances just like Settings importer
        // After all trades: persist and MIRROR to top-level + nested settings.*
        const finalRB  = Number.isFinite(finalRBNumber) ? round2(Number(finalRBNumber)) : round2(runningBalance);
        const fileDelta = finalRB - startRB;
        const finalPRB  = round2(startPRB + fileDelta);
    
        const isAnyProp = propFirm || isPropFunded;

    
        // Known/previous maximum loss limit from either level (do not change its value)
        const mllExisting =
          (s && (s["Maximum Loss Limit"] ?? s["Max Loss Limit"])) ??
          (account?.["Maximum Loss Limit"] ?? account?.["Max Loss Limit"]);
    
          const rbOut = Number.isFinite(finalRBNumber) ? finalRBNumber : runningBalance;

          const updates = {
            // mirror running balance
            "Running Balance": rbOut,
            "settings.Running Balance": rbOut,
          };
          if (isAnyProp) {
            const newPropRB = round2(startPRB + fileDelta);
            // mirror prop running balance
            updates["Prop Running Balance"] = newPropRB;
            updates["settings.Prop Running Balance"] = newPropRB;
          }

        if (mllExisting !== undefined && mllExisting !== null && mllExisting !== "") {
          const mll = Number(mllExisting);
         }
        await updateAccountSettings(userId, accountId, updates);


    dlog("final account updates", updates);

    return { imported, skipped };
  }

  // ------------------------------
  // Trapound (mirror Settings behavior, newest-first)
  // ------------------------------
  if (platform === "trapound") {
    let lastActualBalanceFromFile = NaN;         // last AB seen in *file*
    let lastImportedActualBalance = NaN;         // last AB from a row we actually import

    for (const rawRow of rows) {
      // Keep file’s last AB in case we need a fallback later
      const abFileSide = numFrom(rawRow, ["Actual Balance", "Balance", "Account Balance"]);
      if (Number.isFinite(abFileSide)) lastActualBalanceFromFile = abFileSide;
    }

    // Import newest-first, like before
    for (const r of rows.slice().reverse()) {
      // Pull values with tolerant keys
      const dateStr   = valFrom(r, ["Date", "Trade Date"]);
      const product   = valFrom(r, ["Product", "Symbol", "Instrument"]);
      const position  = valFrom(r, ["Position", "Side"]);
      const sizeRaw   = valFrom(r, ["Size", "Qty", "Quantity", "Lots"]);
      const entryTime = valFrom(r, ["Entry Time", "Entry"]);
      const exitTime  = valFrom(r, ["Exit Time", "Exit"]);
      const duration  = valFrom(r, ["Duration", "Duration (h:mm)"]);

      const productNorm = String(product || "").trim();
      if (productNorm) {
        const rowIsForex = isForexProduct(productNorm);
        if (!rowAllowedForAccount(rowIsForex)) {
          skipped++;
          continue;
        }
      }

      // Numbers
      const sizeNum   = Number(parseSheetNumber(sizeRaw));
      let   reward    = numFrom(r, ["Actual Reward", "Reward"]);
      const resultTxt = String(valFrom(r, ["Result"])).toLowerCase();

      // If Reward was missing, try to derive from balances
      const abFromFile = numFrom(r, ["Actual Balance", "Balance", "Account Balance"]);
      const bbtSaved   = numFrom(r, ["Balance Before Trade", "Balance Before"]);
      if (!Number.isFinite(reward) && Number.isFinite(abFromFile) && Number.isFinite(bbtSaved)) {
        reward = round2(abFromFile - bbtSaved);
      }

      // Respect "Loss" string if present
      if (resultTxt.includes("loss") && Number.isFinite(reward) && reward > 0) {
        reward = -Math.abs(reward);
      }
      if (!Number.isFinite(reward)) reward = 0;    // conservative default

      // Derive Balance Before if needed
      const balanceBefore = Number.isFinite(bbtSaved)
        ? bbtSaved
        : (Number.isFinite(abFromFile) ? round2(abFromFile - reward) : "");

      // Skip truly empty rows (prevents the “1 empty record”)
      const isRowEmpty =
        [dateStr, product, position, entryTime, exitTime].every(v => !String(v || "").trim()) &&
        !Number.isFinite(abFromFile) &&
        !Number.isFinite(reward);
      if (isRowEmpty) continue;

      // Dedupe (with new robust fingerprint)
      const fp = buildFingerprintTrapound(r);
      if (existingFingerprints.has(fp)) { skipped++; continue; }

      const entryPriceCsv = numFrom(r, ["Entry Price", "EntryPrice"]);
      const exitPriceCsv  = numFrom(r, ["Exit Price", "ExitPrice"]);

      // Compute Ticks/Pips if missing
      let ticksOut = numFrom(r, ["Ticks"]);
      let pipsOut  = numFrom(r, ["Pips"]);
      if (!Number.isFinite(ticksOut)) ticksOut = null;
      if (!Number.isFinite(pipsOut))  pipsOut  = null;

      if (ticksOut === null && !isForexProduct(product)) {
        const tv = tickValueFor(getProduct(product));
        if (tv && sizeNum > 0 && Number.isFinite(reward)) {
          ticksOut = round1(reward / (tv * sizeNum));
        }
      }
      if (pipsOut === null && isForexProduct(product)) {
        const entryPrice = numFrom(r, ["Entry Price", "EntryPrice"]);
        const exitPrice  = numFrom(r, ["Exit Price", "ExitPrice"]);
        if (Number.isFinite(entryPrice) && Number.isFinite(exitPrice)) {
          const ps = pipSizeFor(product);
          const side = (String(position || "").toLowerCase() === "sell") ? "sell" : "buy";
          const signedPips = side === "buy"
            ? (exitPrice - entryPrice) / ps
            : (entryPrice - exitPrice) / ps;
          pipsOut = round1(signedPips);
        }
      }

      const notes  = pickField(r, "Notes", "Note", "Notes & Lessons");
      const lesson = pickField(r, "Lesson", "Lessons");

      // --- DETECT MANNY'S SHEET ---
      const headerString = Object.keys(r).join(" ").toLowerCase();
      const isMannySheet = headerString.includes("edited by you") || 
                           headerString.includes("potential risk") || 
                           headerString.includes("max lot size");

      // 1. Get Planned Risk
      let totalRiskRaw = parseSheetNumber(valFrom(r, ["Total Risk", "Potential Risk", "Planned Risk"]));
      if (Number.isFinite(totalRiskRaw)) {
        totalRiskRaw = Math.abs(totalRiskRaw); 
      }

      // 2. Clear Actual Risk Budget for Manny's Sheet
      let riskBudgetRaw = parseSheetNumber(valFrom(r, ["Percent of Balance", "Risk %", "Risk Budget"]));
      
      if (isMannySheet) {
        riskBudgetRaw = ""; // <--- This forces the UI to render a dash/blank instead of $2.50
      }

      // 3. Get Potential Reward & RRR
      const potentialRaw = valFrom(r, ["Potential", "Potential Reward"]);
      const rrrRaw = valFrom(r, ["Actual Risk to Reward", "Risk To Reward Ratio (Ex 1:2)", "RRR"]);

      const tradeObj = {
        "Trade #": Date.now() + imported,
        "Account Type": valFrom(r, ["Account Type"]) || "",
        "Product": valFrom(r, ["Product", "Symbol", "Instrument"]) || "",
        "Position": valFrom(r, ["Position", "Side"]) || "",
        "Size": valFrom(r, ["Size", "Qty", "Quantity", "Lots", "Lot Size", "Lot Size (Max Lot Size = 1000)"]) || "",
        "Actual Percent Growth": valFrom(r, ["Actual Percent Growth", "Percentage Growth/Risk"]) || "",
        "Result": valFrom(r, ["Result"]) || "",

        "Actual Reward": reward,
        "Actual Balance": abFromFile,
        "Balance Before Trade": balanceBefore,

        "Date": valFrom(r, ["Date", "Trade Date"]) || "",
        "Entry Time": valFrom(r, ["Entry Time", "Entry"]) || "",
        "Exit Time": valFrom(r, ["Exit Time", "Exit"]) || "",
        "Duration (h:mm)": valFrom(r, ["Duration", "Duration (h:mm)"]) || "",
        "Potential": potentialRaw || "",
        "IdealSL": valFrom(r, ["Ideal Stop Loss", "IdealSL"]) || "",

        "Entry Price": Number.isFinite(entryPriceCsv) ? entryPriceCsv : "",
        "Exit Price":  Number.isFinite(exitPriceCsv)  ? exitPriceCsv  : "",

        "Links_Screenshots": valFrom(r, ["Links_Screenshots"]) || "",
        "Session": valFrom(r, ["Session"]) || "",
        "Entry Strategy": valFrom(r, ["Entry Strategy"]) || "",
        "Exit Strategy": valFrom(r, ["Exit Strategy"]) || "",
        "Timeframe": valFrom(r, ["Timeframe", "Time Frame", "TF"]) || "",

        "Did I follow my Rules?": valFrom(r, ["Did I follow my Rules?"]) || "",
        "Grade": valFrom(r, ["Grade"]) || "",
        "Mood_Mindset": valFrom(r, ["Mood_Mindset"]) || "",

        "Stop Loss_Take Profit": valFrom(r, ["Stop Loss_Take Profit"]) || "",
        "Total Risk": totalRiskRaw,
        "Percent of Balance": riskBudgetRaw,
        "Actual Risk to Reward": rrrRaw || "",

        "Notes": notes,
        "Lesson": lesson,
        "Win": reward > 0,
        "Loss": reward < 0,
        "created": new Date(),
        "importFingerprint": fp
      };

      if (ticksOut !== null) tradeObj["Ticks"] = ticksOut;
      if (pipsOut  !== null) tradeObj["Pips"]  = pipsOut;

      await appendTrade(userId, accountId, tradeObj);
      existingFingerprints.add(fp);
      imported++;

      if (Number.isFinite(abFromFile)) lastImportedActualBalance = abFromFile;
    }

    // Persist balances (prefer the last *imported* trade's Actual Balance)
    const chosenFinalAB = Number.isFinite(lastImportedActualBalance)
      ? lastImportedActualBalance
      : lastActualBalanceFromFile;

    if (Number.isFinite(chosenFinalAB)) {
      const finalRB  = round2(chosenFinalAB);
      const delta    = finalRB - startRB;
      const finalPRB = round2(startPRB + delta);
      const isAnyProp = Boolean(s.isProp || s.isPropFirm || s.isPropFunded);

      const updates = {
        "Running Balance": finalRB,
        "settings.Running Balance": finalRB,
      };
      if (isAnyProp) {
        updates["Prop Running Balance"]          = finalPRB;
        updates["settings.Prop Running Balance"] = finalPRB;
      }
      await updateAccountSettings(userId, accountId, updates);
      dlog("[importCsvByPlatform] final updates", updates);
    }

    return { imported, skipped };
  }

  if (platform === "rithmic") {
    // Rithmic Positions + Fills report

    // Split into sections so we can parse the two CSV tables separately
    const rawLines = text.replace(/\r/g, "").split("\n");
    const lines = rawLines.map(l => l.trimEnd());

    const findIdx = (label) =>
      lines.findIndex((l) => l && l.trim().toLowerCase() === label);

    const posIdx   = findIdx("positions");
    const fillsIdx = findIdx("fills");

    const parseSection = (startIdx, stopLabelLower) => {
      if (startIdx === -1 || startIdx + 1 >= lines.length) return [];
      const sectionLines = [];
      for (let i = startIdx + 1; i < lines.length; i++) {
        const raw = lines[i];
        const trimmed = raw.trim();
        if (!trimmed) {
          if (sectionLines.length) break;
          continue;
        }
        if (stopLabelLower && trimmed.toLowerCase() === stopLabelLower) break;
        sectionLines.push(raw);
      }
      if (!sectionLines.length) return [];
      const csvText = sectionLines.join("\n");
      const { data } = Papa.parse(csvText, { header: true, skipEmptyLines: "greedy" });
      return Array.isArray(data) ? data : [];
    };

    const positionRows = parseSection(posIdx, "fills");
    const fillRows     = parseSection(fillsIdx, null);

    // Group positions + fills by Symbol
    const bySymbol = new Map();


    for (const p of positionRows) {
      const sym = String(p["Symbol"] || "").trim();
      if (!sym) continue;
      const current = bySymbol.get(sym) || {};
      current.position = p;
      bySymbol.set(sym, current);
    }

    for (const f of fillRows) {
      const sym = String(f["Symbol"] || "").trim();
      if (!sym) continue;
      const current = bySymbol.get(sym) || {};
      if (!current.fills) current.fills = [];
      current.fills.push(f);
      bySymbol.set(sym, current);
    }

    for (const [symbol, group] of bySymbol.entries()) {
      const position = group.position || {};
      const fills    = Array.isArray(group.fills) ? group.fills : [];
      if (!position && !fills.length) continue;

      // NEW: enforce account type: Rithmic is mostly futures, but be defensive
      const rowIsForex = isForexProduct(symbol);
      if (!rowAllowedForAccount(rowIsForex)) {
        skipped++;
        continue;
      }

      // PnL from Positions table
      let reward = 0;
      if (position) {
        const closed = parseSheetNumber(position["Closed Profit/Loss"]);
        const total  = parseSheetNumber(position["Total Profit/Loss"]);
        if (Number.isFinite(closed) && closed !== 0) reward = closed;
        else if (Number.isFinite(total)) reward = total;
      }

      // If we still have neither fills nor non‑zero reward, skip
      if (!fills.length && !reward) continue;

      // Sort fills by timestamp
      const sortedFills = fills.slice().sort((a, b) => {
        const aTs = new Date(String(a["Fill Time Stamp"] || "").trim());
        const bTs = new Date(String(b["Fill Time Stamp"] || "").trim());
        return aTs - bTs;
      });
      const first = sortedFills[0] || {};
      const last  = sortedFills[sortedFills.length - 1] || first;

      // Date (from Trade Date ccyymmdd or fallback to first Fill Time Stamp)
      const rawDate = String(
        first["Trade Date (ccyymmdd)"] ||
        position["Trade Date (ccyymmdd)"] ||
        ""
      ).trim();
      let dateIso = "";
      if (/^\d{8}$/.test(rawDate)) {
        const yyyy = rawDate.slice(0, 4);
        const mm   = rawDate.slice(4, 6);
        const dd   = rawDate.slice(6, 8);
        dateIso    = `${yyyy}-${mm}-${dd}`;
      } else {
        const stamp = String(first["Fill Time Stamp"] || "").trim();
        if (stamp.length >= 10) dateIso = stamp.slice(0, 10);
      }

      const entryStamp = String(first["Fill Time Stamp"] || "").trim();
      const exitStamp  = String(last["Fill Time Stamp"] || entryStamp).trim();

      const entryTime24 = entryStamp.split(" ")[1] || "";
      const exitTime24  = exitStamp.split(" ")[1] || entryTime24;

      const entryTime = toAmPm(entryTime24);
      const exitTime  = toAmPm(exitTime24);

      // Side + size
      const sideRaw = String(first["Buy/Sell"] || "").toUpperCase();
      const positionSide =
        sideRaw.startsWith("B") ? "Buy" :
        sideRaw.startsWith("S") ? "Sell" : "";

      let size = parseSheetNumber(position["Net Quantity"]);
      if (!size) size = parseSheetNumber(first["Fill Size"]);

      if (!size && fills.length) {
        // Fallback: min(buys, sells) from fill sizes
        let buys = 0, sells = 0;
        for (const f of fills) {
          const q = parseSheetNumber(f["Fill Size"]);
          const s = String(f["Buy/Sell"] || "").toUpperCase();
          if (s.startsWith("B")) buys += q;
          else if (s.startsWith("S")) sells += q;
        }
        size = Math.min(buys, sells) || buys || sells || 0;
      }

      // Optional: ticks/pips
      let ticksOut = null;
      let pipsOut  = null;
      if (Number.isFinite(reward) && size > 0) {
        if (isForexProduct(symbol)) {
          const ep = parseSheetNumber(first["Fill Price"]);
          const xp = parseSheetNumber(last["Fill Price"]);
          const pipSize = pipSizeFor(symbol);
          if (pipSize > 0 && ep !== 0 && xp !== 0) {
            const dir = positionSide === "Sell" ? -1 : 1;
            pipsOut = round1(((xp - ep) / pipSize) * dir);
          }
        } else {
          const tv = tickValueFor(symbol);
          if (tv) {
            ticksOut = round1(reward / (tv * size));
          }
        }
      }

      // Ledgers
      const beforeLedger = Number.isFinite(runningBalance) ? runningBalance : 0;
      const afterLedger  = Number.isFinite(reward)
        ? round2(beforeLedger + reward)
        : beforeLedger;

      runningBalance = afterLedger;

      const isAnyProp = Boolean(s.isProp || s.isPropFirm || s.isPropFunded);
      if (isAnyProp && Number.isFinite(reward)) {
        propRunningBalance = round2(propRunningBalance + reward);
      }

      // Fingerprint: per-symbol + day + first/last fill id
      const fp = (
        `rithmic|${symbol}|${dateIso}|` +
        `${String(first["Fill Id"] || "").trim()}|` +
        `${String(last["Fill Id"] || "").trim()}`
      ).toLowerCase();

      if (fp && existingFingerprints.has(fp)) {
        skipped++;
        continue;
      }

      const tradeDoc = {
        "Trade #": Date.now() + imported,
        "Date": dateIso,
        "Product": symbol,                       // e.g. "MGCZ5"
        "Position": positionSide,
        "Size": size || "",
        "Entry Time": entryTime,
        "Exit Time": exitTime,
        "Duration": calculateDuration(entryStamp, exitStamp),
        "Entry Price": first["Fill Price"] || "",
        "Exit Price":  last["Fill Price"] || "",
        "Actual Reward": reward,
        "Balance Before Trade": beforeLedger,
        "Actual Balance": String(afterLedger.toFixed(2)),
        "Win": reward > 0,
        "Loss": reward < 0,
        "Notes": "Imported from Rithmic Positions/Fills CSV.",
        "created": new Date(),
        "importFingerprint": fp,
      };
      if (ticksOut !== null) tradeDoc["Ticks"] = ticksOut;
      if (pipsOut  !== null) tradeDoc["Pips"]  = pipsOut;

      await appendTrade(userId, accountId, tradeDoc);
      existingFingerprints.add(fp);
      imported++;
    }

    // Mirror final balances to the account doc
    if (imported > 0) {
      const updates = {
        "Running Balance": runningBalance,
        "settings.Running Balance": runningBalance,
      };
      const isAnyProp = Boolean(s.isProp || s.isPropFirm || s.isPropFunded);
      if (isAnyProp) {
        updates["Prop Running Balance"]          = propRunningBalance;
        updates["settings.Prop Running Balance"] = propRunningBalance;
      }
      await updateAccountSettings(userId, accountId, updates);
    }

    return { imported, skipped };
  }

  // ------------------------------
  // Quantower (Futures / FX fills export)
  // ------------------------------
  if (platform === "quantower") {
    const hasFinal =
      typeof finalBalance === "string" &&
      finalBalance.trim() !== "" &&
      isFinite(Number(finalBalance));
    const finalRBNumber = hasFinal ? Number(finalBalance) : NaN;

    // Sort oldest → newest so RB/PRB move forward in time
    const sorted = rows
      .filter((r) => String(valFrom(r, ["Symbol"], "")).trim() !== "")
      .slice()
      .sort((a, b) => {
        const aTs = new Date(valFrom(a, ["Date/Time"], "")).getTime();
        const bTs = new Date(valFrom(b, ["Date/Time"], "")).getTime();
        return (aTs || 0) - (bTs || 0);
      });

    for (const row of sorted) {
      const dtRaw      = valFrom(row, ["Date/Time"]);
      const symbolFull = valFrom(row, ["Symbol"]);
      const sideRaw    = valFrom(row, ["Side"]);
      const qty        = numFrom(row, ["Quantity"]);

      // Basic sanity checks
      if (!dtRaw || !symbolFull || !Number.isFinite(qty) || qty === 0) {
        skipped++;
        continue;
      }

      const productCode = getProduct(symbolFull) || symbolFull;
      const rowIsForex  = isForexProduct(symbolFull);
      if (!rowAllowedForAccount(rowIsForex)) {
        // Respect account type: forex account ↔ forex symbols, futures ↔ non‑forex
        skipped++;
        continue;
      }

      // Dedupe
      const fp = buildFingerprintQuantower(row);
      if (existingFingerprints.has(fp)) {
        skipped++;
        continue;
      }

      // P&L: prefer Net P/L, fallback to Gross P/L
      let reward = numFrom(row, ["Net P/L"]);
      if (!Number.isFinite(reward) || Math.abs(reward) < 1e-8) {
        reward = numFrom(row, ["Gross P/L"]);
      }
      const ticksRaw = numFrom(row, ['Gross P/L,ticks']);

      const hasReward = Number.isFinite(reward) && Math.abs(reward) > 1e-8;
      const hasTicks  = Number.isFinite(ticksRaw) && Math.abs(ticksRaw) > 1e-8;

      // Skip pure “no-op” legs (no realized PnL and no ticks)
      if (!hasReward && !hasTicks) {
        skipped++;
        continue;
      }

      if (!Number.isFinite(reward)) reward = 0;

      // Date/Time → ISO date + hh:mm for toAmPm()
      let dateOut = "";
      let time24  = "";
      if (dtRaw) {
        const d = new Date(dtRaw);
        if (!isNaN(d.getTime())) {
          dateOut = formatDate(dtRaw); // uses your existing helper
          const hh = String(d.getHours()).padStart(2, "0");
          const mm = String(d.getMinutes()).padStart(2, "0");
          time24 = `${hh}:${mm}`;
        } else {
          const parts = String(dtRaw).trim().split(" ");
          dateOut = formatDate(parts[0]);
          time24 = parts.slice(1).join(" ");
        }
      }

      const isShort  = String(sideRaw || "").toLowerCase().startsWith("s") || qty < 0;
      const position = isShort ? "Sell" : "Buy";
      const size     = Math.abs(qty);

      const beforeLedger = runningBalance;
      const afterLedger  = round2(beforeLedger + reward);

      const growthPct = computePercentGrowth({
        isPropEval,
        isPropFunded,
        reward,
        rbBefore: beforeLedger,
        prbBefore: propRunningBalance,
      });

      const tradeObj = {
        "Trade #": valFrom(row, ["Trade ID", "Order ID"], Date.now() + imported),
        "Date": dateOut || formatDate(dtRaw),
        "Product": productCode,
        "Position": position,
        "Size": size,
        "Entry Time": toAmPm(time24),
        "Exit Time": toAmPm(time24),
        // We only have one timestamp per fill, so duration is unknown / 0.
        "Duration": "",

        "Actual Reward": round2(reward),
        "Balance Before Trade": beforeLedger,
        "Actual Balance": String(afterLedger.toFixed(2)),
        "Actual Percent Growth": `${round2(growthPct).toFixed(2)}%`,
        "Percent of Balance": null,

        "Win": reward > 0,
        "Loss": reward < 0,

        "Notes": `Imported from Quantower ${symbolFull}`.trim(),
        "created": new Date(),
        "importFingerprint": fp,
      };

      if (hasTicks) {
        tradeObj["Ticks"] = ticksRaw;
      }

      await appendTrade(userId, accountId, tradeObj);

      runningBalance     = afterLedger;
      propRunningBalance = round2(propRunningBalance + reward);
      existingFingerprints.add(fp);
      imported++;
    }

    // Final account updates (same pattern as NinjaTrader)
    const rbOut  = Number.isFinite(finalRBNumber)
      ? round2(finalRBNumber)
      : round2(runningBalance);
    const prbOut = propRunningBalance;

    const updates = {};
    if (Number.isFinite(rbOut)) {
      updates["Running Balance"]          = rbOut;
      updates["settings.Running Balance"] = rbOut;
    }
    if (Number.isFinite(prbOut)) {
      updates["Prop Running Balance"]          = prbOut;
      updates["settings.Prop Running Balance"] = prbOut;
    }

    await updateAccountSettings(userId, accountId, updates);
    dlog("[importCsvByPlatform] Quantower final updates", updates);

    return { imported, skipped };
  }



// ------------------------------
// NinjaTrader (Futures & Forex)
// ------------------------------
if (platform === "ninjatrader") {
  const hasFinal =
    typeof finalBalance === "string" &&
    finalBalance.trim() !== "" &&
    isFinite(Number(finalBalance));
  const finalRBNumber = hasFinal ? Number(finalBalance) : NaN;

  for (const row of rows) {
    // Core fields
    const instrument = valFrom(row, ["Instrument", "Symbol"]);
    const qty        = numFrom(row, ["Qty", "Quantity", "Size"]);
    const entryTs    = valFrom(row, ["Entry time", "Entry Time"]);
    const exitTs     = valFrom(row, ["Exit time",  "Exit Time"]);
    const profitStr  = valFrom(row, ["Profit", "Net profit", "P&L", "PnL"]);

    const hasCore =
      String(instrument || "").trim() &&
      Number.isFinite(qty) &&
      String(entryTs || "").trim() &&
      String(exitTs || "").trim();
    if (!hasCore) continue;

    // Normalize instrument → product code ("ES DEC25" → "ES")
    const rawInstr    = String(instrument || "").trim();
    const productCode = rawInstr.split(" ")[0] || rawInstr;

    // Enforce account type: forex account only gets forex symbols, futures account only gets non‑forex
    const rowIsForex = isForexProduct(productCode);
    if (!rowAllowedForAccount(rowIsForex)) {
      skipped++;
      continue;
    }

    // Dedupe
    const fp = buildFingerprintNinjatrader(row);
    if (existingFingerprints.has(fp)) { skipped++; continue; }

    // Net PnL (Profit is already net of commissions/fees)
    const actReward = round2(money(profitStr));

    // Times and date
    const entered = splitDateTime(entryTs);
    const exited  = splitDateTime(exitTs);

    const beforeLedger = round2(runningBalance);
    const growthPct    = computePercentGrowthDebug({
      isPropEval, isPropFunded,
      reward: actReward,
      rbBefore: beforeLedger,
      prbBefore: round2(propRunningBalance),
    });

    // Position side
    const posRaw   = valFrom(row, ["Market pos.", "Market pos", "Market position", "Position", "Side"]);
    const isShort  = /short|sell/i.test(String(posRaw || ""));
    const position = isShort ? "Sell" : "Buy";

    // NEW: Entry / Exit price import
    const entryPriceNum = parseSheetNumber(valFrom(row, ["Entry price", "Entry Price"]));
    const exitPriceNum  = parseSheetNumber(valFrom(row, ["Exit price",  "Exit Price"]));

    // Ticks (futures) or Pips (forex)
    let ticks = null;
    let pips  = null;

    if (!rowIsForex) {
      // Futures → derive ticks from net PnL and tick value
      const tv = tickValueFor(productCode);
      if (tv && qty && Number.isFinite(actReward)) {
        const rawTicks = actReward / (tv * qty);
        if (Number.isFinite(rawTicks)) ticks = Math.round(rawTicks);
      }
    } else {
      // Forex → derive pips from entry/exit price
      const ps = pipSizeFor(productCode);
      if (ps && Number.isFinite(entryPriceNum) && Number.isFinite(exitPriceNum)) {
        const diff    = isShort
          ? entryPriceNum - exitPriceNum
          : exitPriceNum - entryPriceNum;
        const rawPips = diff / ps;
        if (Number.isFinite(rawPips)) pips = Math.round(rawPips);
      }
    }

    const afterLedger = round2(beforeLedger + actReward);

    const tradeObj = {
      "Trade #": valFrom(row, ["Trade number", "Trade #", "Trade"], Date.now() + imported),
      "Date": formatDate(entered.date || entryTs),
      "Product": productCode,
      "Position": position,
      "Size": qty,
      "Entry Time": toAmPm(entered.time),
      "Exit Time": toAmPm(exited.time),
      "Duration": calculateDuration(entryTs, exitTs),

      // Store prices so your Trade Details / Edit screens can show them
      "Entry Price": Number.isFinite(entryPriceNum) ? entryPriceNum : null,
      "Exit Price":  Number.isFinite(exitPriceNum)  ? exitPriceNum  : null,

      "Actual Reward": actReward,
      "Balance Before Trade": beforeLedger,
      "Actual Balance": String(afterLedger.toFixed(2)),

      "Actual Percent Growth": `${round2(growthPct).toFixed(2)}%`,
      "Percent of Balance": null,

      "Win": actReward > 0,
      "Loss": actReward < 0,

      "Notes": `Imported from NinjaTrader ${valFrom(row, ["Trade number", "Trade #", "Trade"], "")}`.trim(),
      "created": new Date(),
      "importFingerprint": fp,
    };

    if (ticks !== null) tradeObj["Ticks"] = ticks;
    if (pips  !== null) tradeObj["Pips"]  = pips;

    await appendTrade(userId, accountId, tradeObj);

    runningBalance     = afterLedger;
    propRunningBalance = round2(propRunningBalance + actReward);

    existingFingerprints.add(fp);
    imported++;
  }

  // Final account updates
  const rbOut = Number.isFinite(finalRBNumber)
    ? round2(finalRBNumber)
    : round2(runningBalance);

  const delta    = rbOut - startRB;
  const finalPRB = round2(startPRB + delta);
  const isAnyProp = Boolean(propFirm || isPropFunded);

  const updates = {
    "Running Balance": rbOut,
    "settings.Running Balance": rbOut,
  };
  if (isAnyProp) {
    updates["Prop Running Balance"]          = finalPRB;
    updates["settings.Prop Running Balance"] = finalPRB;
  }

  await updateAccountSettings(userId, accountId, updates);
  dlog("[importCsvByPlatform] NinjaTrader final updates", updates);

  return { imported, skipped };
}

// ------------------------------
  // TradingView (broker-agnostic CSV export)
  // ------------------------------
  if (platform === "tradingview") {
    // 1) Filter to filled orders with a symbol, qty and fill price
    const filledRows = rows.filter((r) => {
      const status = String(valFrom(r, ["Status"], "")).toLowerCase();
      if (status && status !== "filled") return false;

      const symbol = valFrom(r, ["Symbol", "Instrument", "Ticker"], "");
      if (!symbol) return false;

      const price = numFrom(r, ["Fill Price", "Price"]);
      const qty   = numFrom(r, ["Qty", "Quantity", "Size"]);
      if (!Number.isFinite(price) || !Number.isFinite(qty) || qty === 0) return false;

      // NEW: enforce forex/futures account type for TradingView imports
      const symbolFull    = String(symbol || "").trim();
      const parts         = symbolFull.split(":");
      const symAfterColon = (parts[1] || parts[0] || "").trim().toUpperCase();
      const rowIsForex    =
        symbolFull.toUpperCase().startsWith("OANDA:") || isForexProduct(symAfterColon);

      if (!rowAllowedForAccount(rowIsForex)) return false;

      return true;
    });

    // Group by symbol
    const bySymbol = new Map();
    for (const r of filledRows) {
      const symbol = valFrom(r, ["Symbol", "Instrument", "Ticker"], "");
      const key = String(symbol || "").trim() || "__NO_SYMBOL__";
      if (!bySymbol.has(key)) bySymbol.set(key, []);
      bySymbol.get(key).push(r);
    }

    // Sort key: use placing time, fall back to closing time
    const sortKey = (r) => {
      const primary  = valFrom(r, ["Placing Time", "PlacingTime", "Placed Time", "Time", "Date"], "");
      const fallback = valFrom(r, ["Closing Time", "ClosingTime"], "");
      return String(primary || fallback || "");
    };

    // --- NEW LOGIC: Staging array for chronological sorting ---
    const extractedTrades = [];

    for (const [symbolRaw, group] of bySymbol.entries()) {
      group.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

      // Pair orders 2‑by‑2: [entry, exit], [entry, exit], ...
      for (let i = 0; i < group.length - 1; i += 2) {
        const entryRow = group[i];
        const exitRow  = group[i + 1];

        const entrySide = String(valFrom(entryRow, ["Side", "Direction"], "")).toLowerCase();
        const exitSide  = String(valFrom(exitRow,  ["Side", "Direction"], "")).toLowerCase();
        if (!entrySide || !exitSide || entrySide === exitSide) { skipped++; continue; }

        const qtyEntry = numFrom(entryRow, ["Qty", "Quantity", "Size"]);
        const qtyExit  = numFrom(exitRow,  ["Qty", "Quantity", "Size"]);
        const qty = Number.isFinite(qtyEntry) && qtyEntry !== 0 ? qtyEntry : qtyExit;
        if (!Number.isFinite(qty) || qty === 0) { skipped++; continue; }

        const entryPrice = numFrom(entryRow, ["Fill Price", "Price"]);
        const exitPrice  = numFrom(exitRow,  ["Fill Price", "Price"]);
        if (!Number.isFinite(entryPrice) || !Number.isFinite(exitPrice)) { skipped++; continue; }

        // Determine product + whether this is forex or futures
        const symbolFull = String(symbolRaw || "").trim();
        const parts = symbolFull.split(":");
        const symAfterColon = (parts[1] || parts[0] || "").trim().toUpperCase();

        const isForex = symbolFull.toUpperCase().startsWith("OANDA:") || isForexProduct(symAfterColon);
        const product = isForex
          ? symAfterColon.replace(/[^A-Z]/g, "")   // e.g., "GBPJPY"
          : getProduct(symAfterColon);            // e.g., "MNQ1!" → "MNQ"

        const direction = entrySide.startsWith("s") ? "sell" : "buy";

        if (!rowAllowedForAccount(isForex)) {
          skipped++;
          continue;
        }

        // Times
        const entryTs = valFrom(entryRow, ["Closing Time", "Placing Time", "Placed Time", "Time"], "");
        const exitTs  = valFrom(exitRow,  ["Closing Time", "Placing Time", "Placed Time", "Time"], "");

        const entered = splitDateTime(entryTs);
        const exited  = splitDateTime(exitTs);
        const dateOut = formatDate(entered.date || exited.date || entryTs || exitTs);

        const entryTime = entered.time;
        const exitTime  = exited.time;
        const duration  = calculateDuration(entryTs, exitTs);

        // ---------------- P&L / pips / ticks ----------------
        const absQty = Math.abs(qty);
        let sizeDisplay = absQty;

        let actReward = 0;
        let ticksOut  = null;
        let pipsOut   = null;

        if (!isForex) {
          // Futures: derive ticks from price difference, then P&L from tick value
          const code     = getProduct(product);
          const tickSize = tickSizeFor(code);
          const tv       = tickValueFor(code);

          if (tickSize && tv) {
            const rawTicks = direction === "buy"
              ? (exitPrice - entryPrice) / tickSize
              : (entryPrice - exitPrice) / tickSize;

            const signedTicks = Math.round(rawTicks);
            ticksOut  = signedTicks;
            actReward = round2(tv * signedTicks * absQty);
          }
        } else {
          // Forex: derive pips from entry/exit price, then approximate $ P&L
          const ps = pipSizeFor(product);
          if (ps) {
            const rawPips = direction === "buy"
              ? (exitPrice - entryPrice) / ps
              : (entryPrice - exitPrice) / ps;

            const signedPips = rawPips;
            pipsOut = round1(signedPips);

            // TradingView qty is usually UNITS -> convert to lots when it looks large
            const looksLikeUnits = absQty > 100;           // e.g. 10,000 / 100,000 / etc.
            const lotsRaw        = looksLikeUnits ? absQty / 100000 : absQty;
            const lots           = round2(lotsRaw);

            sizeDisplay = lots;                            // what you'll see in the Size column

            // Approx P&L: about $10 per pip per standard lot
            const pipValuePerLot = 10;
            actReward = round2(pipsOut * pipValuePerLot * lots);
          }
        }

        // Fingerprint for dedupe
        const fp = [
          "tradingview",
          symbolFull.toLowerCase(),
          dateOut,
          entryTime,
          exitTime,
          String(qty),
          String(entryPrice),
          String(exitPrice),
        ].join("|");

        if (existingFingerprints.has(fp)) { skipped++; continue; }

        // --- NEW LOGIC: Push to staging array instead of updating ledger/Firebase yet ---
        extractedTrades.push({
          entryTs, exitTs, dateOut, product, direction, absQty, 
          sizeDisplay, actReward, pipsOut, ticksOut, entryTime, 
          exitTime, duration, entryPrice, exitPrice, fp, isForex
        });
      } // end 2-by-2 orders loop
    } // end bySymbol loop

    // --- NEW LOGIC: Sort all extracted trades globally by chronological order ---
    extractedTrades.sort((a, b) => {
      const timeA = new Date(a.entryTs || a.exitTs).getTime() || 0;
      const timeB = new Date(b.entryTs || b.exitTs).getTime() || 0;
      return timeA - timeB;
    });

    // --- NEW LOGIC: Calculate ledgers in chronological order and push to Firebase ---
    for (const t of extractedTrades) {
      // Balances & % growth
      const beforeLedger = round2(runningBalance);
      const growthPct    = computePercentGrowthDebug({
        isPropEval,
        isPropFunded,
        reward: t.actReward,
        rbBefore: beforeLedger,
        prbBefore: round2(propRunningBalance),
      });
      const afterLedger  = round2(beforeLedger + t.actReward);

      // Outcome flags: prefer reward, fall back to pips/ticks
      let win  = false;
      let loss = false;
      if (Number.isFinite(t.actReward) && t.actReward !== 0) {
        win  = t.actReward > 0;
        loss = t.actReward < 0;
      } else if (t.pipsOut !== null) {
        win  = t.pipsOut > 0;
        loss = t.pipsOut < 0;
      } else if (t.ticksOut !== null) {
        win  = t.ticksOut > 0;
        loss = t.ticksOut < 0;
      }

      const tradeObj = {
        "Trade #": Date.now() + imported,
        "Date": t.dateOut,
        "Product": t.product,
        "Position": t.direction === "sell" ? "Sell" : "Buy",
        "Size": t.isForex ? t.sizeDisplay : t.absQty,
        "Result": win ? "Win" : (loss ? "Loss" : ""),

        // PnL / balances
        "Actual Reward": t.actReward,
        "Balance Before Trade": beforeLedger,
        "Actual Balance": String(afterLedger.toFixed(2)),
        "Actual Percent Growth": `${round2(growthPct).toFixed(2)}%`,

        // Times
        "Entry Time": t.entryTime,
        "Exit Time": t.exitTime,
        "Duration (h:mm)": t.duration,

        // Prices
        "Entry Price": t.entryPrice,
        "Exit Price":  t.exitPrice,

        "Win": win,
        "Loss": loss,
        "Notes": "Imported from TradingView",
        "created": new Date(),
        "importFingerprint": t.fp,
      };

      if (t.pipsOut !== null)  tradeObj["Pips"]  = t.pipsOut;
      if (t.ticksOut !== null) tradeObj["Ticks"] = t.ticksOut;

      await appendTrade(userId, accountId, tradeObj);

      runningBalance     = afterLedger;
      propRunningBalance = round2(propRunningBalance + t.actReward);

      existingFingerprints.add(t.fp);
      imported++;
    }

    // Update account-level balances from the ledger
    const finalRB  = round2(runningBalance);
    const delta    = finalRB - startRB;
    const finalPRB = round2(startPRB + delta);
    const isAnyProp = Boolean(s.isProp || s.isPropFirm || s.isPropFunded || propFirm || isPropFunded);

    const updates = {
      "Running Balance": finalRB,
      "settings.Running Balance": finalRB,
    };
    if (isAnyProp) {
      updates["Prop Running Balance"]          = finalPRB;
      updates["settings.Prop Running Balance"] = finalPRB;
    }
    await updateAccountSettings(userId, accountId, updates);

    return { imported, skipped };
  }




  // Unknown platform (guard)
  throw new Error(`Unsupported platform: ${platform}`);
}
